import * as React from 'react';
import { createContext, useContext, useState } from 'react';
import Spinner from './Spinner';
import WarningIcon from '../../svg/warning.svg';

interface PageLoadingContextType {
  isLoading: boolean;
  setLoading: (loading: boolean, message?: string) => void;
  loadingMessage: string;
  error: string | null;
  setError: (message: string | null) => void;
}

const PageLoadingContext = createContext<PageLoadingContextType | undefined>(undefined);

export const usePageLoading = (): PageLoadingContextType => {
  const context = useContext(PageLoadingContext);
  if (!context) {
    throw new Error('usePageLoading must be used within a PageLoadingProvider');
  }
  return context;
};

interface PageLoadingProviderProps {
  children: React.ReactNode;
}

export const PageLoadingProvider: React.FC<PageLoadingProviderProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState<string | null>(null);

  const setLoading = (loading: boolean, message = 'Loading...') => {
    setIsLoading(loading);
    setLoadingMessage(message);
  };

  return (
    <PageLoadingContext.Provider value={{ isLoading, setLoading, loadingMessage, error, setError }}>
      <div className="relative">
        {children}
        {isLoading && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <Spinner message={loadingMessage} overlay={false} />
            </div>
          </div>
        )}
        {error && !isLoading && (
          <div className="fixed top-4 inset-x-0 z-50 flex justify-center px-4 pointer-events-none">
            <div
              role="alert"
              className="pointer-events-auto flex w-full max-w-xl items-start gap-3 rounded-md border border-red-300 bg-red-50 p-4 shadow-lg"
            >
              <WarningIcon className="h-5 w-5 flex-shrink-0 text-red-500" />
              <p className="flex-1 text-sm text-red-800">{error}</p>
              <button
                type="button"
                onClick={() => setError(null)}
                aria-label="Dismiss error"
                className="text-red-400 hover:text-red-600"
              >
                &times;
              </button>
            </div>
          </div>
        )}
      </div>
    </PageLoadingContext.Provider>
  );
};